import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./Profile.css";
import FieldRenderer from "../profileEngine/FieldRenderer";
import { useAuth } from "../hooks/useAuth";

const AdminProfileView: React.FC = () => {
  const { userId, templateId } = useParams<{
    userId: string;
    templateId: string;
  }>();

  const navigate = useNavigate();
  const { roles } = useAuth();
  const canReveal = Array.isArray(roles?.permissions)
    ? roles.permissions.includes("profiles.sensitive")
    : false;

  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState<any>(null);
  const [submission, setSubmission] = useState<any>(null);
  // The profile owner's identity (from the Users module / Auth0). Registration
  // fields render from this so the admin sees THIS user's name/email/phone —
  // not the viewing admin's session. Always current, no save-time snapshot.
  const [registrationUser, setRegistrationUser] = useState<any>(null);
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [currentStepIndex, setCurrentStepIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [certHistoryOpen, setCertHistoryOpen] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const templateRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/templates`,
          { credentials: "include" },
        );
        const templates = await templateRes.json();
        const tpl = templates.find(
          (t: any) => String(t.id) === String(templateId),
        );
        setTemplate(tpl || null);

        if (tpl) {
          const submissionRes = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/profile-submissions/${userId}/${templateId}`,
            { credentials: "include" },
          );
          const data = await submissionRes.json();
          setSubmission(data || null);
          setFormData(data?.data || {});
        }

        // Look up the profile owner in the org user list for Registration
        // fields. The Users module already holds the Auth0-linked identity, so
        // this is always populated by the time a user has a profile to review.
        try {
          const usersRes = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/approved_users`,
            { credentials: "include" },
          );
          const users = await usersRes.json();
          const match = Array.isArray(users)
            ? users.find((u: any) => u.user_id === userId)
            : null;
          setRegistrationUser(match || null);
        } catch (e) {
          console.error("Failed to load registration user", e);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userId, templateId]);

  const updateField = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // Union: any field currently marked sensitive in the LIVE template stays
  // sensitive in the rendered view, even if the locked snapshot was taken
  // before the flag was set. Without this, fields flipped to sensitive
  // after an old submission would render as plain text + lack the reveal
  // button, despite the admin's intent.
  //
  // MUST be declared before any early return — Rules of Hooks require the
  // same hook call order on every render. Placing useMemo after the
  // `if (loading) return …` below caused the first render (loading=true)
  // to call one fewer hook than subsequent renders, crashing the component.
  const liveSensitiveKeys = React.useMemo(() => {
    const out = new Set<string>();
    for (const s of template?.schema?.steps || []) {
      for (const f of s.fields || []) {
        if (f && f.sensitive === true && typeof f.key === "string") {
          out.add(f.key);
        }
      }
    }
    return out;
  }, [template]);

  if (loading) return <div style={{ padding: 32 }}>Loading...</div>;

  // Locked submissions render against the snapshot the backend froze at
  // submit time so admin-side template edits don't retroactively rewrite
  // history. Reopened/in-progress profiles (signatureMeta=null) keep
  // rendering against the live template so the admin can edit/extend.
  const lockedSchema =
    submission?.signatureMeta && submission?.templateSnapshot?.schema;
  const schema =
    lockedSchema || template?.schema || template?.data || {};
  const steps = schema.steps || [];
  const signatureMeta = submission?.signatureMeta || {};

  const withSensitiveUnion = (field: any) =>
    field && liveSensitiveKeys.has(field.key) && !field.sensitive
      ? { ...field, sensitive: true }
      : field;

  const shouldShowField = (field: any) => {
    if (!field.visibleWhen || field.visibleWhen.length === 0) return true;

    return field.visibleWhen.every((rule: any) => {
      const controllingValue = formData[rule.fieldKey];
      if (!controllingValue) return false;

      if (rule.operator === "equals") return controllingValue === rule.value;

      if (rule.operator === "notEquals") return controllingValue !== rule.value;

      return true;
    });
  };

  const saveAdminEdits = async () => {
    if (!template || !submission) return;

    await fetch(`${process.env.REACT_APP_BACKEND_URL}/profile-submissions`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId,
        profileTemplateId: template.id,
        data: formData,
        signatureMeta: submission.signatureMeta,
      }),
    });

    setMode("view");
    setCurrentStepIndex(null);
  };

  return (
    <div className="profile-page">
      <div className="profile-wizard">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2 className="center-title">Full Profile (Admin View)</h2>

          <div style={{ display: "flex", gap: "12px" }}>
            {submission &&
              (submission.signatureMeta ? (
                <button
                  className="btn-warning"
                  onClick={() => {
                    setReopenReason("");
                    setShowReopenModal(true);
                  }}
                >
                  Reopen Profile
                </button>
              ) : (
                <button className="btn-secondary" disabled>
                  Profile Unlocked
                </button>
              ))}

            <button
              className="btn-add"
              onClick={() => navigate("/manage-profiles")}
            >
              Back
            </button>
          </div>
        </div>

        {template &&
          steps.map((step: any, index: number) => (
            <div key={step.id} className="profile-card">
              <div
                className="card-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3
                  className={
                    step.scope === "internal"
                      ? "step-title-internal"
                      : "step-title-public"
                  }
                >
                  {step.label}
                </h3>

                {step.scope === "internal" && (
                  <button
                    className="edit-button"
                    onClick={() => {
                      setMode("edit");
                      setCurrentStepIndex(index);
                    }}
                  >
                    ✏️
                  </button>
                )}
              </div>

              {mode === "edit" && currentStepIndex === index ? (
                <>
                  <div className="two-col-grid">
                    {(step.fields || [])
                      .filter((field: any) => shouldShowField(field))
                      .map((field: any) => (
                        <FieldRenderer
                          key={field.key}
                          field={field}
                          value={formData[field.key]}
                          mode="edit"
                          onChange={(val) => updateField(field.key, val)}
                          user={registrationUser}
                        />
                      ))}
                  </div>

                  <div style={{ marginTop: 24 }}>
                    <button className="btn-save" onClick={saveAdminEdits}>
                      Save
                    </button>
                  </div>
                </>
              ) : (
                <div className="two-col-grid">
                  {(step.fields || [])
                    .filter((field: any) => shouldShowField(field))
                    .map((field: any) => (
                      <FieldRenderer
                        key={field.key}
                        field={withSensitiveUnion(field)}
                        value={formData[field.key]}
                        mode="view"
                        user={registrationUser}
                        userId={userId}
                        profileTemplateId={templateId}
                        canReveal={canReveal}
                      />
                    ))}
                </div>
              )}
            </div>
          ))}

        {/* Declaration */}
        {submission?.signatureMeta && (
          <div className="profile-card" style={{ background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, padding: 16 }}>
            <div className="card-header" style={{ justifyContent: "center" }}>
              <h3 style={{ textAlign: "center" }}>Declaration</h3>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ color: "#fff", fontSize: "0.9rem" }}>Accuracy of Information – <span style={{ color: "#1faa59" }}>Confirmed</span></div>
              <div style={{ color: "#fff", fontSize: "0.9rem" }}>Ongoing Obligation to Update – <span style={{ color: "#1faa59" }}>Confirmed</span></div>
              <div style={{ color: "#fff", fontSize: "0.9rem" }}>Internal Use Acknowledgement – <span style={{ color: "#1faa59" }}>Confirmed</span></div>
              <div style={{ color: "#fff", fontSize: "0.9rem" }}>Consequences of False Information – <span style={{ color: "#1faa59" }}>Confirmed</span></div>
              <div style={{ marginTop: 8 }}>
                <p className="label" style={{ fontSize: "0.75rem", color: "#9aa0a6", textTransform: "uppercase", letterSpacing: "0.04em" }}>Certified By</p>
                <div style={{ color: "#fff" }}>{signatureMeta.typedName || "—"}</div>
              </div>
            </div>
          </div>
        )}

        {/* Certification Details */}
        {submission?.signatureMeta && (
          <div style={{ background: "#1a1a1a", border: "1px solid #1faa59", borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#1faa59", marginBottom: 10, textAlign: "center" }}>Certification Details</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, textAlign: "center" }}>
              <div><span style={{ fontSize: "0.75rem", color: "#9aa0a6", textTransform: "uppercase" as const, letterSpacing: "0.04em" }}>Certified By: </span><span style={{ color: "#fff" }}>{signatureMeta.typedName || "—"}</span></div>
              <div><span style={{ fontSize: "0.75rem", color: "#9aa0a6", textTransform: "uppercase" as const, letterSpacing: "0.04em" }}>Signed: </span><span style={{ color: "#fff" }}>{signatureMeta.timestamp || "—"}</span></div>
              <div><span style={{ fontSize: "0.75rem", color: "#9aa0a6", textTransform: "uppercase" as const, letterSpacing: "0.04em" }}>IP Address: </span><span style={{ color: "#fff" }}>{signatureMeta.ipAddress || "—"}</span></div>
              <div><span style={{ fontSize: "0.75rem", color: "#9aa0a6", textTransform: "uppercase" as const, letterSpacing: "0.04em" }}>User Agent: </span><span style={{ color: "#9aa0a6", fontSize: "0.7rem" }}>{signatureMeta.userAgent || "—"}</span></div>
            </div>
          </div>
        )}

        {/* Previous Certifications — collapsible */}
        {submission?.certHistory?.length > 0 && (
          <div style={{ background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, marginBottom: 16 }}>
            <div
              style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
              onClick={() => setCertHistoryOpen(!certHistoryOpen)}
            >
              <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#9aa0a6" }}>
                Previous Certifications ({submission.certHistory.length})
              </span>
              <span style={{ color: "#9aa0a6", fontSize: "0.8rem" }}>
                {certHistoryOpen ? "▲" : "▼"}
              </span>
            </div>
            {certHistoryOpen && (
              <div style={{ padding: "0 16px 16px" }}>
                {submission.certHistory.slice().reverse().map((entry: any, i: number) => (
                  <div key={i} style={{ borderTop: i > 0 ? "1px solid #2a2a2a" : "none", paddingTop: i > 0 ? 8 : 0, marginTop: i > 0 ? 8 : 0, display: "flex", flexDirection: "column", gap: 4, textAlign: "center" }}>
                    <div><span style={{ fontSize: "0.75rem", color: "#9aa0a6", textTransform: "uppercase" as const, letterSpacing: "0.04em" }}>Certified By: </span><span style={{ color: "#fff", fontSize: "0.8rem" }}>{entry.typedName || entry.name || "—"}</span></div>
                    <div><span style={{ fontSize: "0.75rem", color: "#9aa0a6", textTransform: "uppercase" as const, letterSpacing: "0.04em" }}>Signed: </span><span style={{ color: "#fff", fontSize: "0.8rem" }}>{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : "—"}</span></div>
                    <div><span style={{ fontSize: "0.75rem", color: "#9aa0a6", textTransform: "uppercase" as const, letterSpacing: "0.04em" }}>IP: </span><span style={{ color: "#9aa0a6", fontSize: "0.75rem" }}>{entry.ipAddress || "—"}</span></div>
                    {entry.reason && (
                      <div><span style={{ fontSize: "0.75rem", color: "#9aa0a6", textTransform: "uppercase" as const, letterSpacing: "0.04em" }}>Reopen Reason: </span><span style={{ color: "#d4a017", fontSize: "0.8rem" }}>{entry.reason}</span></div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {showReopenModal && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Reopen Profile</h2>
              <button
                className="btn-cancel"
                onClick={() => setShowReopenModal(false)}
              >
                Close
              </button>
            </div>
            <div className="smrtdocs-modal-body">
              <div style={{ marginBottom: 12, color: "#9aa0a6", fontSize: "0.85rem" }}>
                Reopening this profile will allow the user to resubmit.
              </div>
              <div style={{ marginBottom: 6, fontSize: "0.75rem", color: "#9aa0a6", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Reason for Reopening *
              </div>
              <textarea
                className="manage-input"
                placeholder="Enter reason for reopening this profile..."
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                style={{ width: "100%", height: 80, padding: "8px 10px", resize: "vertical", boxSizing: "border-box" }}
              />
            </div>
            <div style={{ padding: "12px 20px", display: "flex", justifyContent: "flex-end", gap: 8, borderTop: "1px solid #555" }}>
              <button
                className="btn-cancel"
                onClick={() => setShowReopenModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-save"
                style={{ background: "#d4a017", borderColor: "#d4a017" }}
                disabled={!reopenReason.trim()}
                onClick={async () => {
                  await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/profile-submissions/reopen`,
                    {
                      method: "POST",
                      credentials: "include",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        userId,
                        profileTemplateId: templateId,
                        status: "reopened",
                        reason: reopenReason.trim(),
                      }),
                    },
                  );

                  setShowReopenModal(false);
                  window.location.reload();
                }}
              >
                Confirm Reopen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProfileView;
